import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingRecordDetailsCustomer, BookingStatus } from '@navio/nsdk';
import { NoddiLink, NoddiStepper, StepperStep } from 'noddi-ui-web';
import {
  customerRoutes,
  customerTimelineWheelPickups,
  getIsPaidInAdvance,
  getIsWindShieldRepairJob,
  getJobCompletedAndPaymentLeft,
  getShouldGiveFeedback,
  isMarkedPaidOutsideApp
} from 'noddi-util';
import { getLongAddressName } from 'noddi-web';
import { RefObject } from 'react';
import { BookingEstimatedArrivalInfo } from './BookingEstimatedArrivalInfo';
import { InitiatePaymentSessionButton } from './InitiatePaymentSessionButton';
import { ViewFeedbackModal } from './ViewFeedbackModal';
import { WheelPickupSigningCard } from './WheelPickupSigningCard';
import { getConfirmedStepDescription } from './confirmedStep';
import { getTirePickupStep } from './tirePickupStep';

enum BookingStatusStep {
  Confirmed = 'Confirmed',
  WheelPickupSigning = 'Wheel pickup signing',
  TirePickup = 'Tire pickup',
  Assigned = 'Assigned',
  ServiceInProgress = 'Service in progress',
  DamageReport = 'Damage report',
  FeedbackGiven = 'Feedback given',
  Completed = 'Completed'
}

function getStepsWithTranslation({
  booking,
  activeStepEnum,
  paymentButtonRef
}: {
  booking: BookingRecordDetailsCustomer;
  activeStepEnum: BookingStatusStep;
  paymentButtonRef?: RefObject<HTMLDivElement | null>;
}): StepperStep[] {
  const isWindShieldRepair = getIsWindShieldRepairJob(booking);

  const isAssignedStepActive = activeStepEnum === BookingStatusStep.Assigned;
  const isServiceInProgressStepActive = activeStepEnum === BookingStatusStep.ServiceInProgress;

  // Check if wheel pickup signing is needed
  const wheelPickupSigningOrders = booking?.wheelSetPickups || [];
  const hasWheelPickupSigning = wheelPickupSigningOrders.length > 0;
  const lastDocument = wheelPickupSigningOrders
    .sort((a, b) => new Date(b.signingUrlExpiresAt).getTime() - new Date(a.signingUrlExpiresAt).getTime())
    .find((order) => order);
  const hasSigned = !!lastDocument?.documentsSigned.length;
  const signingUrl = lastDocument?.signingUrl;
  const showPoaContent = !hasSigned && signingUrl && new Date(lastDocument?.signingUrlExpiresAt) > new Date();

  const hasGivenFeedback = !!booking.serviceFeedback;

  // Get pickup location for power of attorney
  const pickupLocation =
    lastDocument?.tireHotelLocation?.name ||
    (lastDocument?.pickupAddress ? getLongAddressName(lastDocument.pickupAddress) : null);

  // Power of attorney description helpers
  const getPoaDescription = () => {
    if (showPoaContent) {
      return pickupLocation
        ? t`Please sign the power of attorney to pick up your tires from ${pickupLocation}.`
        : t`Please sign the power of attorney to pick up your tires.`;
    }
    return pickupLocation
      ? t`Power of attorney has been signed to pick up your tires from ${pickupLocation}.`
      : t`Power of attorney has been signed to pick up your tires.`;
  };

  const tirePickupStep = getTirePickupStep({ booking, key: BookingStatusStep.TirePickup });

  const steps: StepperStep[] = [
    {
      label: t`Booking confirmed`,
      description: getConfirmedStepDescription(booking),
      key: BookingStatusStep.Confirmed
    },
    ...(hasWheelPickupSigning
      ? [
          {
            label: t`Power of attorney`,
            description: getPoaDescription(),
            key: BookingStatusStep.WheelPickupSigning,
            content: <WheelPickupSigningCard wheelSetPickups={booking.wheelSetPickups} variant='button' />
          }
        ]
      : []),
    ...(tirePickupStep ? [tirePickupStep] : []),
    ...(isWindShieldRepair
      ? [
          {
            label: t`Damage report`,
            description: t`You will receive a damage report shortly. Make sure to fill it out before we arrive.`,
            key: BookingStatusStep.DamageReport
          }
        ]
      : []),
    // Without a customer visit nobody arrives: the tire pickup is the only physical step.
    ...(booking.hasCustomerVisit
      ? [
          {
            label: t`Estimated arrival`,
            key: BookingStatusStep.Assigned,
            isLive: isAssignedStepActive,
            content: <BookingEstimatedArrivalInfo booking={booking} isActiveStep={isAssignedStepActive} />
          },
          {
            label: t`Service in progress`,
            // eslint-disable-next-line noddi/no-banned-ui-terms -- consumer copy: end customers read 'technician'; 'worker' is the backoffice term
            description: t`Our technician has arrived and is working on your vehicle.`,
            key: BookingStatusStep.ServiceInProgress,
            isLive: isServiceInProgressStepActive
          }
        ]
      : []),
    getCompletedStep(booking, paymentButtonRef),
    ...(hasGivenFeedback
      ? [
          {
            label: t`Feedback given`,
            description: t`Thank you for sharing your experience with us!`,
            key: BookingStatusStep.FeedbackGiven,
            content: <ViewFeedbackModal feedback={booking.serviceFeedback!} />
          }
        ]
      : [])
  ];

  return steps;
}

// we mark all as completed but we show different messages depending on the status
function getCompletedStep(
  booking: BookingRecordDetailsCustomer,
  paymentButtonRef?: RefObject<HTMLDivElement | null>
): StepperStep {
  const {
    cancelledAt,
    completedAt,
    order: { isFullyPaid, isManuallyInvoiced },
    isFullyUnableToComplete,
    isPartiallyUnableToComplete
  } = booking;

  const isJobCompletedAndPaymentLeft = getJobCompletedAndPaymentLeft(booking);
  const isFeedbackLeft = getShouldGiveFeedback(booking);

  if (cancelledAt) {
    return {
      label: t`Booking cancelled`,
      description: t`Your booking has been cancelled.`,
      key: BookingStatusStep.Completed
    };
  }
  if (isFullyUnableToComplete) {
    return {
      label: t`Booking unable to complete`,
      // eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate glyph inside a customer-facing copy string, where a NoddiIcon element cannot be embedded
      description: t`⚠️ Your booking has been unable to complete.`,
      key: BookingStatusStep.Completed,
      content: (
        <div className='flex flex-col gap-2'>
          {booking.commentsUnableToCompletePublic && (
            <p className='text-sm text-muted-foreground'>{booking.commentsUnableToCompletePublic}</p>
          )}
          <NoddiLink to={customerRoutes.unableToCompleteDetails.getPath({ slug: booking.slug })}>
            <Trans>See more details</Trans>
          </NoddiLink>
        </div>
      )
    };
  }
  if (isPartiallyUnableToComplete) {
    return {
      label: t`Booking partially completed`,
      // eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate glyph inside a customer-facing copy string, where a NoddiIcon element cannot be embedded
      description: t`⚠️ Some services in your booking were unable to be completed.`,
      key: BookingStatusStep.Completed,
      content: (
        <div className='flex flex-col gap-2'>
          {booking.commentsUnableToCompletePublic && (
            <p className='text-sm text-muted-foreground'>{booking.commentsUnableToCompletePublic}</p>
          )}
          <NoddiLink
            endIcon='ArrowRight'
            size='small'
            to={customerRoutes.unableToCompleteDetails.getPath({ slug: booking.slug })}
          >
            <Trans>See more details</Trans>
          </NoddiLink>
        </div>
      )
    };
  }

  if (isJobCompletedAndPaymentLeft) {
    return {
      label: t`Service completed`,
      description: t`Your service has been completed. Please complete your payment.`,
      key: BookingStatusStep.Completed,
      content: <InitiatePaymentSessionButton booking={booking} ref={paymentButtonRef} />
    };
  }

  if (isFeedbackLeft) {
    return {
      label: t`Service completed`,
      description: t`Your service has been completed. We would love to hear your thoughts.`,
      key: BookingStatusStep.Completed,
      content: (
        <NoddiLink to={customerRoutes.feedbackForBooking.getPath({ slug: booking.slug })}>
          <Trans>Give feedback</Trans>
        </NoddiLink>
      )
    };
  }

  const shopCollected = isMarkedPaidOutsideApp(booking.order);
  const serviceDone = Boolean(completedAt) || booking.status.value === BookingStatus.COMPLETED;

  // The step shows before the visit too: a covered booking must not read as done, nor as charged later.
  // Status too: a completed booking can lack `completedAt`. Shop collection is not a Navio charge.
  if ((getIsPaidInAdvance(booking.order) || shopCollected) && !serviceDone) {
    return {
      label: t`Service completed`,
      description: t`You paid in advance, so there is nothing to pay when the service is done.`,
      key: BookingStatusStep.Completed
    };
  }

  // A refund still due leaves a prepaid order not "fully paid", yet nothing is owed.
  if (isFullyPaid || getIsPaidInAdvance(booking.order)) {
    return {
      // eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate emoji in customer-facing copy: it carries the tone of the message, it is not a status a NoddiIcon could show
      label: `${t`Service completed`} 🎉`,
      description: t`Your service has been completed and payment has been processed.`,
      key: BookingStatusStep.Completed
    };
  }

  if (isManuallyInvoiced) {
    return {
      // eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate emoji in customer-facing copy: it carries the tone of the message, it is not a status a NoddiIcon could show
      label: `${t`Service completed`} 🎉`,
      description: t`Your service has been completed. You will receive an invoice shortly.`,
      key: BookingStatusStep.Completed
    };
  }
  return {
    label: t`Service completed`,
    description: t`Your service has been completed. Please complete your payment.`,
    key: BookingStatusStep.Completed
  };
}

function getActiveStep(booking: BookingRecordDetailsCustomer): BookingStatusStep {
  const {
    communicatedArrivalEnd,
    communicatedArrivalStart,
    wheelSetPickups,
    isFullyUnableToComplete,
    isPartiallyUnableToComplete,
    completedAt,
    cancelledAt,
    startedAt,
    serviceFeedback
  } = booking;

  // Check if wheel pickup signing is needed and not signed yet
  if (wheelSetPickups && wheelSetPickups?.length > 0) {
    const lastDocument = wheelSetPickups
      .sort((a, b) => new Date(b.signingUrlExpiresAt).getTime() - new Date(a.signingUrlExpiresAt).getTime())
      .find((order) => order);
    const hasSigned = !!lastDocument?.documentsSigned.length;
    const signingUrl = lastDocument?.signingUrl;
    const showPoaContent = !hasSigned && signingUrl && new Date(lastDocument?.signingUrlExpiresAt) > new Date();

    if (showPoaContent) {
      return BookingStatusStep.WheelPickupSigning;
    }
  }

  const isBookingClosed = isFullyUnableToComplete || isPartiallyUnableToComplete || completedAt || cancelledAt;
  const hasArrivalNews = startedAt || (communicatedArrivalEnd && communicatedArrivalStart);
  if (customerTimelineWheelPickups(booking).length > 0 && !isBookingClosed && !hasArrivalNews) {
    return BookingStatusStep.TirePickup;
  }

  // If service is completed or cancelled or unable to complete
  if (isBookingClosed) {
    // If feedback has been given, show that as the active step
    if (serviceFeedback) {
      return BookingStatusStep.FeedbackGiven;
    }
    // Otherwise show the completed step (which may include give feedback action)
    return BookingStatusStep.Completed;
  }

  if (startedAt) {
    return BookingStatusStep.ServiceInProgress;
  }
  if (communicatedArrivalEnd && communicatedArrivalStart) {
    return BookingStatusStep.Assigned;
  }
  if (getIsWindShieldRepairJob(booking)) {
    return BookingStatusStep.DamageReport;
  }
  return BookingStatusStep.Confirmed;
}

interface BookingStatusUpdatesProps {
  booking: BookingRecordDetailsCustomer;
  paymentButtonRef?: RefObject<HTMLDivElement | null>;
}

export const BSlugStatusUpdates = ({ booking, paymentButtonRef }: BookingStatusUpdatesProps) => {
  const activeStepEnum = getActiveStep(booking);
  const steps = getStepsWithTranslation({ booking, activeStepEnum, paymentButtonRef });
  const activeStep = steps.findIndex((step) => step.key.includes(activeStepEnum));

  return <NoddiStepper steps={steps} activeStep={activeStep} orientation='vertical' />;
};
